import { createContext, useContext, useState, useEffect, ReactNode, Dispatch, SetStateAction } from 'react';
import { useLocation } from 'wouter';

// Assuming 'storage' is imported and provides methods like:
// saveToken(token: string), getToken(): Promise<string | null>
// saveRefreshToken(refreshToken: string), getRefreshToken(): Promise<string | null>
// saveUser(user: User), getUser(): Promise<User | null>
// clearAll(): Promise<void>
// For this example, we'll mock it. In a real app, import it.
export const storage = {
  saveToken: async (token: string) => {
    localStorage.setItem('auth_token', token);
    return Promise.resolve();
  },
  getToken: async () => Promise.resolve(localStorage.getItem('auth_token')),
  saveRefreshToken: async (refreshToken: string) => {
    localStorage.setItem('refresh_token', refreshToken);
    return Promise.resolve();
  },
  getRefreshToken: async () => Promise.resolve(localStorage.getItem('refresh_token')),
  saveUser: async (user: User) => {
    localStorage.setItem('auth_user', JSON.stringify(user));
    return Promise.resolve();
  },
  getUser: async () => {
    const userJson = localStorage.getItem('auth_user');
    return Promise.resolve(userJson ? JSON.parse(userJson) : null);
  },
  clearAll: async () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('auth_user');

    // Selective sessionStorage clearing - only remove auth-related keys
    const authKeys = ['redirect_after_login', 'pendingBooking', 'booking_error', 'pending_demographics'];
    authKeys.forEach(key => sessionStorage.removeItem(key));

    return Promise.resolve();
  },
};

interface User {
  id: number;
  email: string;
  role: 'admin' | 'therapist' | 'client';
  firstName?: string;
  lastName?: string;
  userType: 'admin' | 'therapist' | 'client';
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  setUser: Dispatch<SetStateAction<User | null>>;
  setToken: Dispatch<SetStateAction<string | null>>;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ success: boolean; error?: string; isProcessingBooking?: boolean }>;
  register: (userData: any, userType: 'admin' | 'therapist' | 'client') => Promise<{ success: boolean; error?: string; isProcessingBooking?: boolean }>;
  loginWithOAuth: (provider: string, code: string, userType?: string) => Promise<{ success: boolean; error?: string; isProcessingBooking?: boolean }>;
  logout: () => Promise<void>;
  logoutAllSessions: () => Promise<{ success: boolean; error?: string }>;
  refreshUser: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // This should NEVER happen if AuthProvider properly guards rendering
    // If you see this error, a component is being evaluated before AuthProvider mounts
    console.error('❌ CRITICAL: useAuth called outside AuthProvider');
    console.error('   This means a component is importing/rendering before AuthProvider is ready');
    console.trace('   Call stack:');

    // Return safe defaults to prevent crashes, but this indicates a structural issue
    return {
      user: null,
      token: null,
      isLoading: true, // Signal that auth is not ready
      isAuthenticated: false,
      setUser: () => {},
      setToken: () => {},
      login: async () => ({ success: false, error: 'Auth not initialized' }),
      register: async () => ({ success: false, error: 'Auth not initialized' }),
      loginWithOAuth: async () => ({ success: false, error: 'Auth not initialized' }),
      logout: async () => {},
      logoutAllSessions: async () => ({ success: false, error: 'Auth not initialized' }),
      refreshUser: async () => {},
      refreshToken: async () => false,
    } as AuthContextType;
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTimerId, setRefreshTimerId] = useState<NodeJS.Timeout | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshAttempt, setLastRefreshAttempt] = useState<number>(0);
  const [, setLocation] = useLocation();

  const isAuthenticated = !!user && !!token;

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🚀 AuthProvider initialization started at:', new Date().toISOString());
      console.log('   Current URL:', window.location.href);
      console.log('   Referrer:', document.referrer);

      try {
        // Check if localStorage is persistent (Safari ITP check)
        const testStoragePersistence = () => {
          try {
            const testKey = '_storage_test_' + Date.now();
            localStorage.setItem(testKey, 'test');
            const retrieved = localStorage.getItem(testKey);
            localStorage.removeItem(testKey);

            if (!retrieved) {
              console.warn('⚠️ localStorage may not be persistent in this browser');
              console.warn('⚠️ Safari users: Session may be cleared when closing tabs');

              // Try to persist storage (modern browsers)
              if (navigator.storage && navigator.storage.persist) {
                navigator.storage.persist().then(granted => {
                  console.log(granted ? '✅ Storage persistence granted' : '⚠️ Storage persistence denied');
                });
              }
              return false;
            }
            return true;
          } catch (e) {
            console.error('❌ localStorage not available:', e);
            return false;
          }
        };

        const isPersistent = testStoragePersistence();

        const storedToken = await storage.getToken();
        const storedUser = await storage.getUser();
        const storedRefreshToken = await storage.getRefreshToken();

        console.log('🔄 AuthContext initializing:', {
          hasToken: !!storedToken,
          hasUser: !!storedUser,
          hasRefreshToken: !!storedRefreshToken,
          userEmail: storedUser?.email,
          userId: storedUser?.id,
          userRole: storedUser?.role,
          storagePersistent: isPersistent
        });

        if (storedToken && storedUser && storedRefreshToken) {
          setToken(storedToken);
          setUser(storedUser);
          console.log('✅ Auth state restored from storage:', {
            email: storedUser.email,
            role: storedUser.role
          });

          // Wait 500ms before calling refreshUser to ensure storage operations complete
          await new Promise(resolve => setTimeout(resolve, 500));

          // Verify token is still valid and schedule refresh
          await refreshUser();
          scheduleTokenRefresh();
        } else {
          console.log('⚠️ No complete auth state found in storage:', {
            missingToken: !storedToken,
            missingUser: !storedUser,
            missingRefreshToken: !storedRefreshToken,
            currentUrl: window.location.href,
            referrer: document.referrer,
            timestamp: new Date().toISOString()
          });

          // Debug: Check if storage was recently cleared
          console.log('🔍 Storage inspection:', {
            allLocalStorageKeys: Object.keys(localStorage),
            localStorageLength: localStorage.length,
            sessionStorageKeys: Object.keys(sessionStorage),
            timestamp: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
      } finally {
        setIsLoading(false);
        console.log('✅ AuthProvider initialization completed at:', new Date().toISOString());
        console.log('   Auth state:', { hasUser: !!user, hasToken: !!token });
      }
    };

    initializeAuth();

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && user) {
        console.log('🔍 Tab visible - verifying auth state');
        const storedToken = await storage.getToken();
        const storedRefreshToken = await storage.getRefreshToken();

        if (!storedToken && !storedRefreshToken && user) {
          console.warn('⚠️ Tokens missing - waiting for browser sync...');

          const MAX_VISIBILITY_RETRIES = 3;
          const VISIBILITY_RETRY_DELAY = 2000;

          for (let attempt = 0; attempt < MAX_VISIBILITY_RETRIES; attempt++) {
            await new Promise(resolve => setTimeout(resolve, VISIBILITY_RETRY_DELAY));

            const retryToken = await storage.getToken();
            const retryRefreshToken = await storage.getRefreshToken();

            if (retryToken || retryRefreshToken) {
              console.log(`✅ Tokens restored after browser sync (attempt ${attempt + 1})`);
              await refreshUser();
              return;
            }
            console.warn(`⚠️ Tokens still missing (attempt ${attempt + 1}/${MAX_VISIBILITY_RETRIES})`);
          }

          console.error('❌ Storage cleared by browser after all retries - logging out');
          await logout();
        } else if (storedToken && user) {
          await refreshUser();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Refresh token on user activity (every 5 minutes of activity)
    let lastActivityTime = Date.now();
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    const handleUserActivity = () => {
      const now = Date.now();
      // If 100 minutes have passed since last activity, refresh token
      if (now - lastActivityTime > 100 * 60 * 1000 && user && token) {
        console.log('🔄 User activity detected - refreshing token');
        refreshToken();
      }
      lastActivityTime = now;
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Listen for storage changes from other tabs (logout sync)
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === 'auth_token' && !e.newValue && user) {
        // Token was removed in another tab - sync logout
        console.log('🔄 Auth token removed in another tab - syncing logout');
        setUser(null);
        setToken(null);
        if (refreshTimerId) {
          clearTimeout(refreshTimerId);
          setRefreshTimerId(null);
        }
      } else if (e.key === 'auth_token' && e.newValue && !user) {
        // Token was added in another tab - sync login
        console.log('🔄 Auth token added in another tab - syncing login');
        const storedUser = await storage.getUser();
        if (storedUser) {
          setUser(storedUser);
          setToken(e.newValue);
          scheduleTokenRefresh();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, []); // Empty dependency array - only run once on mount

  const login = async (email: string, password: string, rememberMe: boolean = true): Promise<{ success: boolean; error?: string; isProcessingBooking?: boolean }> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, rememberMe }),
      });

      // Handle non-JSON responses gracefully
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('❌ Login response was not JSON:', { status: response.status, contentType });
        const errorText = await response.text();
        console.error('Login response body:', errorText.substring(0, 200)); // Log first 200 chars
        return { success: false, error: 'Received an unexpected response from the server.' };
      }

      const data = await response.json();

      if (response.ok) {
        const { user: userData, token: userToken, refreshToken: userRefreshToken } = data;

        // STEP 1: Persist tokens FIRST
        await storage.saveToken(userToken);
        await storage.saveRefreshToken(userRefreshToken);
        await storage.saveUser(userData);

        // STEP 2: Update state
        setUser(userData);
        setToken(userToken);

        scheduleTokenRefresh();

        // STEP 3: Check for pending booking
        const hasPendingBooking = sessionStorage.getItem('pendingBooking');
        if (hasPendingBooking) {
          console.log('📋 Login: Processing pending booking...');
          // Set processing flag immediately
          const processingResult = { success: true, isProcessingBooking: true };

          // Process booking in background (don't await to avoid blocking UI)
          handlePendingBookingAfterAuth(userToken).catch(error => {
            console.error('❌ Background booking processing failed:', error);
          });

          return processingResult;
        }

        // Check for pending demographics and save them
        const pendingDemographics = sessionStorage.getItem('pending_demographics');
        if (pendingDemographics) {
          try {
            const demographics = JSON.parse(pendingDemographics);
            console.log('💾 Saving pending demographics after login');

            const profileResponse = await fetch('/api/user/profile', {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${userToken}`
              },
              body: JSON.stringify(demographics)
            });

            if (profileResponse.ok) {
              console.log('✅ Demographics saved successfully');
              sessionStorage.removeItem('pending_demographics');
            } else {
              console.error('❌ Failed to save pending demographics:', profileResponse.status);
            }
          } catch (error) {
            console.error('❌ Error saving pending demographics:', error);
          }
        }

        // Check for pending booking and create it using centralized function
        const pendingBookingStr = sessionStorage.getItem('pendingBooking');
        if (pendingBookingStr) {
          try {
            const bookingData = JSON.parse(pendingBookingStr);
            console.log('📋 Processing pending booking after login:', bookingData);

            // Use centralized booking creation with retry logic
            const result = await createPendingBooking(bookingData, userToken);

            if (result.success) {
              console.log('✅ Pending booking created successfully, booking ID:', result.bookingId);

              // Set redirect based on session type and booking ID
              if (bookingData.sessionType === 'ai') {
                // For AI sessions, add booking confirmation flag
                sessionStorage.setItem('redirect_after_login', `/my-account/sessions?booking_success=true&booking_id=${result.bookingId}&session_type=ai`);
                console.log('📍 Set redirect path for AI booking:', `/my-account/sessions?booking_success=true&booking_id=${result.bookingId}&session_type=ai`);
              } else if (bookingData.sessionType === 'human' || bookingData.sessionType === 'group') {
                const redirectPath = `/my-account/sessions?booking_success=true&booking_id=${result.bookingId}`;
                sessionStorage.setItem('redirect_after_login', redirectPath);
                console.log('📍 Set redirect path for booking:', redirectPath);
              } else {
                sessionStorage.setItem('redirect_after_login', '/my-account/sessions');
              }
            } else {
              console.error('❌ Failed to create pending booking:', result.error);
              sessionStorage.setItem('booking_error', result.error || 'Failed to create booking. Please try again.');
              sessionStorage.setItem('redirect_after_login', '/booking');
            }
          } catch (error: any) {
            console.error('❌ Error processing pending booking:', error);
            sessionStorage.setItem('booking_error', error.message || 'Network error while creating booking. Please try again.');
            sessionStorage.setItem('redirect_after_login', '/booking');
          }
        }

        // Handle redirect after login with role validation
        const redirectPath = sessionStorage.getItem('redirect_after_login');
        if (redirectPath) {
          console.log('Checking redirect path:', redirectPath, 'for user role:', userData.role);

          // Validate redirect path matches user role
          const isAdminPath = redirectPath.startsWith('/admin');
          const isTherapistPath = redirectPath.startsWith('/therapist');

          let shouldRedirect = true;

          if (isAdminPath && userData.role !== 'admin') {
            console.log('❌ Admin path blocked for non-admin user');
            shouldRedirect = false;
          } else if (isTherapistPath && userData.role !== 'therapist') {
            console.log('❌ Therapist path blocked for non-therapist user');
            shouldRedirect = false;
          }

          sessionStorage.removeItem('redirect_after_login');

          if (shouldRedirect) {
            console.log('✅ Redirecting to saved path:', redirectPath);
            setLocation(redirectPath);
          } else {
            // Redirect to appropriate dashboard for user role
            if (userData.role === 'admin') {
              setLocation('/admin');
            } else if (userData.role === 'therapist') {
              setLocation('/therapist-dashboard');
            } else {
              setLocation('/');
            }
          }
        }


        return { success: true };
      } else {
        if (response.status === 423) {
          return { success: false, error: data.message || 'Account temporarily locked due to too many failed attempts.' };
        }
        return { success: false, error: data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const register = async (userData: any, userType: 'admin' | 'therapist' | 'client'): Promise<{ success: boolean; error?: string; isProcessingBooking?: boolean }> => {
    try {
      const response = await fetch(`/api/auth/register/${userType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      // Handle non-JSON responses gracefully
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('❌ Registration response was not JSON:', { status: response.status, contentType });
        const errorText = await response.text();
        console.error('Registration response body:', errorText.substring(0, 200)); // Log first 200 chars
        return { success: false, error: 'Received an unexpected response from the server.' };
      }

      const data = await response.json();

      if (response.ok) {
        const { user: newUser, token: userToken, refreshToken: userRefreshToken } = data;

        // STEP 1: Persist tokens FIRST
        await storage.saveToken(userToken);
        await storage.saveRefreshToken(userRefreshToken);
        await storage.saveUser(newUser);

        // STEP 2: Update state
        setUser(newUser);
        setToken(userToken);

        scheduleTokenRefresh();

        // STEP 3: Check for pending booking
        const hasPendingBooking = sessionStorage.getItem('pendingBooking');
        if (hasPendingBooking) {
          console.log('📋 Register: Processing pending booking...');

          // Process booking in background
          handlePendingBookingAfterAuth(userToken).catch(error => {
            console.error('❌ Background booking processing failed:', error);
          });

          return { success: true, isProcessingBooking: true };
        }

        // Check for pending demographics and save them
        const pendingDemographics = sessionStorage.getItem('pending_demographics');
        if (pendingDemographics) {
          try {
            const demographics = JSON.parse(pendingDemographics);
            console.log('💾 Saving pending demographics after registration');

            const profileResponse = await fetch('/api/user/profile', {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${userToken}`
              },
              body: JSON.stringify(demographics)
            });

            if (profileResponse.ok) {
              console.log('✅ Demographics saved successfully');
              sessionStorage.removeItem('pending_demographics');
            } else {
              console.error('❌ Failed to save pending demographics:', profileResponse.status);
            }
          } catch (error) {
            console.error('❌ Error saving pending demographics:', error);
          }
        }

        // Check for pending booking and create it using centralized function
        const pendingBookingStr = sessionStorage.getItem('pendingBooking');
        if (pendingBookingStr) {
          try {
            const bookingData = JSON.parse(pendingBookingStr);
            console.log('📋 Processing pending booking after registration:', bookingData);

            // Use centralized booking creation with retry logic
            const result = await createPendingBooking(bookingData, userToken);

            if (result.success) {
              console.log('✅ Pending booking created successfully, booking ID:', result.bookingId);

              // Determine redirect path based on session type
              let redirectPath = '/my-account/sessions';
              if (bookingData.sessionType === 'ai') {
                redirectPath = `/ai-session?booking_id=${result.bookingId}`;
                console.log('📍 AI session redirect:', redirectPath);
              } else if (bookingData.sessionType === 'human') {
                redirectPath = `/my-account/sessions?booking_success=true&booking_id=${result.bookingId}`;
                console.log('📍 Human session redirect:', redirectPath);
              } else if (bookingData.sessionType === 'group') {
                redirectPath = `/my-account/sessions?booking_success=true&booking_id=${result.bookingId}`;
                console.log('📍 Group session redirect:', redirectPath);
              }

              // Navigate immediately after booking creation
              console.log('🚀 AuthContext navigating to:', redirectPath);
              setLocation(redirectPath);
            } else {
              console.error('❌ Failed to create pending booking:', result.error);
              sessionStorage.setItem('booking_error', result.error || 'Failed to create booking. Please try again.');
              sessionStorage.setItem('redirect_after_login', '/booking');
            }
          } catch (error: any) {
            console.error('❌ Error processing pending booking:', error);
            sessionStorage.setItem('booking_error', error.message || 'Network error while creating booking. Please try again.');
            sessionStorage.setItem('redirect_after_login', '/booking');
          }
        }

        return { success: true };
      } else {
        return { success: false, error: data.message || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const loginWithOAuth = async (provider: string, code: string, userType: string = 'client'): Promise<{ success: boolean; error?: string; isProcessingBooking?: boolean }> => {
    try {
      // Check if we're already authenticated (tokens already stored from OAuth callback)
      const existingToken = await storage.getToken();
      const existingUser = await storage.getUser();

      let userToken = existingToken;
      let userData = existingUser;

      // If not already authenticated, call the OAuth endpoint
      if (!existingToken || !existingUser || code !== 'already-authenticated') {
        const response = await fetch(`/api/auth/oauth/${provider}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code, userType }),
        });

        // Handle non-JSON responses gracefully
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.error('❌ OAuth login response was not JSON:', { status: response.status, contentType });
          const errorText = await response.text();
          console.error('OAuth login response body:', errorText.substring(0, 200)); // Log first 200 chars
          return { success: false, error: 'Received an unexpected response from the server.' };
        }

        const data = await response.json();

        if (!response.ok) {
          return { success: false, error: data.message || 'OAuth login failed' };
        }

        userToken = data.token;
        userData = data.user;
        const userRefreshToken = data.refreshToken;

        setUser(userData);
        setToken(userToken);

        await storage.saveToken(userToken);
        await storage.saveRefreshToken(userRefreshToken);
        await storage.saveUser(userData);
      }

      scheduleTokenRefresh();

      // Check for pending demographics and save them (before checking booking)
      const pendingDemographics = sessionStorage.getItem('pending_demographics');
      if (pendingDemographics) {
        try {
          const demographics = JSON.parse(pendingDemographics);
          console.log('💾 Saving pending demographics after OAuth');

          const profileResponse = await fetch('/api/user/profile', {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${userToken}`
            },
            body: JSON.stringify(demographics)
          });

          if (profileResponse.ok) {
            console.log('✅ Demographics saved successfully');
            sessionStorage.removeItem('pending_demographics');
          } else {
            console.error('❌ Failed to save pending demographics:', profileResponse.status);
          }
        } catch (error) {
          console.error('❌ Error saving pending demographics:', error);
        }
      }

      // Check for pending booking
      const hasPendingBooking = sessionStorage.getItem('pendingBooking');
      if (hasPendingBooking) {
        console.log('📋 OAuth: Processing pending booking - BLOCKING until complete...');

        // CRITICAL: AWAIT booking creation (blocking)
        await handlePendingBookingAfterAuth(userToken);

        // Return after booking completes
        return { success: true, isProcessingBooking: false };
      }

      return { success: true };
    } catch (error) {
      console.error('OAuth login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const refreshToken = async (retryCount = 0): Promise<boolean> => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 2000;

    if (isRefreshing && retryCount === 0) {
      console.log('⏭️ Token refresh already in progress, skipping');
      return false;
    }

    try {
      if (retryCount === 0) setIsRefreshing(true);

      const storedRefreshToken = await storage.getRefreshToken();
      if (!storedRefreshToken) {
        console.log('⚠️ No refresh token available - logging out');
        await logout();
        return false;
      }

      console.log(`🔄 Attempting to refresh token (attempt ${retryCount + 1}/${MAX_RETRIES})...`);

      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
        credentials: 'include',
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('❌ Token refresh returned non-JSON response');
        console.error('Response status:', response.status);

        if (response.status === 401) {
          console.log('🔄 Refresh token invalid - logging out');
          await logout();
          return false;
        }

        if (retryCount < MAX_RETRIES - 1) {
          console.log(`🔄 Non-JSON response, retrying in ${RETRY_DELAY_MS}ms...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
          return refreshToken(retryCount + 1);
        }
        console.warn('⚠️ Token refresh failed after all retries (non-JSON response)');
        return false;
      }

      const data = await response.json();

      if (response.ok) {
        if (!data.token || !data.refreshToken) {
          console.error('❌ Token refresh response missing required fields:', data);
          return false;
        }

        setToken(data.token);
        await storage.saveToken(data.token);
        await storage.saveRefreshToken(data.refreshToken);
        scheduleTokenRefresh();
        console.log('✅ Token refreshed successfully');
        return true;
      } else {
        console.warn('⚠️ Token refresh failed with status:', response.status);

        if (response.status === 401) {
          console.log('🔄 Refresh token rejected (401) - logging out');
          await logout();
          return false;
        }

        if (retryCount < MAX_RETRIES - 1) {
          console.log(`🔄 Server error ${response.status}, retrying in ${RETRY_DELAY_MS}ms...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
          return refreshToken(retryCount + 1);
        }

        console.warn('⚠️ Token refresh failed after all retries - keeping session alive');
        return false;
      }
    } catch (error) {
      console.error('❌ Token refresh network error:', error);

      if (retryCount < MAX_RETRIES - 1) {
        console.log(`🔄 Network error, retrying in ${RETRY_DELAY_MS}ms...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        return refreshToken(retryCount + 1);
      }

      console.warn('⚠️ Token refresh failed after all retries (network) - keeping session alive');
      return false;
    } finally {
      if (retryCount === 0) setIsRefreshing(false);
    }
  };

  const scheduleTokenRefresh = () => {
    // Clear existing timer
    if (refreshTimerId) {
      clearTimeout(refreshTimerId);
    }

    // Parse token to get actual expiry time
    const parseJwt = (token: string) => {
      try {
        return JSON.parse(atob(token.split('.')[1]));
      } catch {
        return null;
      }
    };

    const currentToken = token || localStorage.getItem('auth_token');
    if (!currentToken) return;

    const tokenPayload = parseJwt(currentToken);
    if (!tokenPayload || !tokenPayload.exp) {
      // Fallback to default 100 min if can't parse
      const timerId = setTimeout(refreshToken, 100 * 60 * 1000);
      setRefreshTimerId(timerId);
      return;
    }

    const expiryTime = tokenPayload.exp * 1000;
    const refreshTime = expiryTime - (20 * 60 * 1000); // 20 min before expiry
    const timeUntilRefresh = refreshTime - Date.now();

    if (timeUntilRefresh > 0) {
      const timerId = setTimeout(async () => {
        const success = await refreshToken();
        if (success) {
          scheduleTokenRefresh();
        }
      }, timeUntilRefresh);
      setRefreshTimerId(timerId);
      console.log(`🔄 Token refresh scheduled in ${Math.round(timeUntilRefresh / 60000)} minutes`);
    } else {
      // Token already expired or about to expire, refresh immediately
      refreshToken();
    }
  };

  const logout = async () => {
    // Clear refresh timer
    if (refreshTimerId) {
      clearTimeout(refreshTimerId);
      setRefreshTimerId(null);
    }

    const currentToken = await storage.getToken();
    const refreshTokenValue = await storage.getRefreshToken();

    // Always try to logout on server, even if token is expired
    if (currentToken || refreshTokenValue) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': currentToken ? `Bearer ${currentToken}` : '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken: refreshTokenValue }),
        });
      } catch (error) {
        console.error('Server logout failed:', error);
        // Continue with client-side cleanup even if server logout fails
      }
    }

    // Always clear client-side state (biometric keychain is intentionally kept
    // so the user can log back in with biometrics next time)
    setUser(null);
    setToken(null);
    // Clear the biometric auto-trigger flag so it fires again if user logs back in this session
    sessionStorage.removeItem('biometricAutoTriggered');
    await storage.clearAll();
  };

  // Centralized booking creation with retry logic
  const createPendingBooking = async (bookingData: any, userToken: string, retryCount = 0): Promise<{ success: boolean; bookingId?: number; error?: string }> => {
      const MAX_RETRIES = 3;

      try {
        console.log(`📋 Creating booking (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        console.log('📋 Booking data:', JSON.stringify(bookingData, null, 2));
        console.log('🔑 Using token:', userToken.substring(0, 20) + '...');

        const bookingResponse = await fetch('/api/bookings', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userToken}`
          },
          body: JSON.stringify({
            sessionType: bookingData.sessionType,
            therapistName: bookingData.therapistName || (bookingData.sessionType === 'ai' ? 'HealthyOwl AI' : 'Wellness Professional'),
            therapistId: bookingData.therapistId || null,
            date: bookingData.date || new Date().toISOString().split('T')[0],
            time: bookingData.time || new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
            timezone: bookingData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            notes: bookingData.notes || 'AI wellness session requested',
            selectedSessionTypeName: bookingData.selectedSessionTypeName || (bookingData.sessionType === 'ai' ? 'AI Wellness Session' : 'Individual Session'),
            status: 'confirmed',
            ageRange: bookingData.ageRange || null,
            relationshipStatus: bookingData.relationshipStatus || null,
            youAre: bookingData.youAre || null,
            occupation: bookingData.occupation || null,
            pricingBreakdown: bookingData.pricingBreakdown || {
              basePrice: 0,
              discountPercent: 0,
              discountAmount: 0,
              taxRate: 0,
              taxAmount: 0,
              finalPrice: 0
            }
          })
        });

        console.log('📥 Booking response status:', bookingResponse.status);
        console.log('📥 Booking response headers:', Object.fromEntries(bookingResponse.headers.entries()));

        // Handle non-JSON responses gracefully
        const contentType = bookingResponse.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.error('❌ Booking creation response was not JSON:', { status: bookingResponse.status, contentType });
          const errorText = await bookingResponse.text();
          console.error('Booking creation response body:', errorText.substring(0, 500));
          throw new Error('Server returned an invalid response. Please try again.');
        }

        if (bookingResponse.ok) {
          const createdBooking = await bookingResponse.json();
          console.log('✅ Booking created successfully:', createdBooking);
          sessionStorage.removeItem('booking_error');
          return { success: true, bookingId: createdBooking.id };
        } else {
          const errorData = await bookingResponse.json();
          console.error('❌ Booking creation failed:', errorData);

          // Don't retry on validation errors or conflicts
          if (bookingResponse.status === 400 || bookingResponse.status === 409) {
            return { success: false, error: errorData.message || 'Booking validation failed' };
          }

          // If it's a server error, throw to trigger retry
          throw new Error(errorData.message || `Server error: ${bookingResponse.status}`);
        }
      } catch (error: any) {
        console.error(`❌ Booking attempt ${retryCount + 1} failed:`, error.message);

        // Categorize error types
        const isAuthError = error.message?.includes('Authentication') ||
                           error.message?.includes('token') ||
                           error.message?.includes('Unauthorized');

        const isNetworkError = error.message?.includes('Failed to fetch') ||
                              error.message?.includes('Network');

        // Don't retry auth errors - user needs to re-authenticate
        if (isAuthError) {
          console.error('❌ Authentication error - stopping retries');
          return {
            success: false,
            error: 'Session expired during booking. Please login and try again.'
          };
        }

        // If max retries reached, return the error
        if (retryCount >= MAX_RETRIES - 1) {
          console.error('❌ Max retries reached for booking creation.');
          return {
            success: false,
            error: isNetworkError
              ? 'Network error - please check your connection and try again.'
              : error.message || 'Failed to create booking after multiple attempts.'
          };
        }

        // Exponential backoff: 500ms, 1s, 2s, 4s, 8s
        const delay = 500 * Math.pow(2, retryCount);
        console.log(`⏳ Retrying booking creation in ${delay / 1000} seconds...`);
        await new Promise(resolve => setTimeout(resolve, delay));

        // Recursively call with incremented retry count
        return createPendingBooking(bookingData, userToken, retryCount + 1);
      }
  };


  // Helper to handle pending booking with timeout
  const handlePendingBookingAfterAuth = async (userToken: string): Promise<void> => {
    const pendingBookingStr = sessionStorage.getItem('pendingBooking');
    if (!pendingBookingStr) return;

    // CRITICAL: Check if booking creation is already in progress (prevents duplicates)
    const creationLock = sessionStorage.getItem('booking_creation_lock');
    if (creationLock) {
      const lockTime = parseInt(creationLock, 10);
      const now = Date.now();
      // Lock expires after 30 seconds (stale lock protection)
      if (now - lockTime < 30000) {
        console.log('⏳ AuthContext: Booking creation already in progress, skipping duplicate');
        return;
      }
      console.log('⚠️ AuthContext: Stale booking lock detected, clearing');
    }

    // Set lock IMMEDIATELY before any async operations
    sessionStorage.setItem('booking_creation_lock', Date.now().toString());
    console.log('🔒 AuthContext: Set booking creation lock');

    // Clear any stale completion flag from previous attempts
    sessionStorage.removeItem('booking_processing');
    console.log('📋 AuthContext: Cleared stale booking_processing flag');

    try {
      const bookingData = JSON.parse(pendingBookingStr);
      console.log('📋 AuthContext: Processing pending booking:', bookingData.sessionType);

      // Verify auth tokens are persisted before creating booking
      const authPersisted = await verifyAuthPersisted();
      if (!authPersisted) {
        throw new Error('Authentication not persisted - please login again');
      }

      console.log('📋 AuthContext: Creating booking...');

      // Create booking with 10-second timeout
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Booking creation timeout (10s)')), 10000)
      );

      const result = await Promise.race([
        createPendingBooking(bookingData, userToken),
        timeoutPromise
      ]);

      if (result.success) {
        console.log('✅ AuthContext: Booking created successfully, ID:', result.bookingId);

        // Set redirect based on session type
        let redirectPath = '';
        if (bookingData.sessionType === 'ai') {
          redirectPath = `/ai-session?booking_id=${result.bookingId}`;
        } else if (bookingData.sessionType === 'human' || bookingData.sessionType === 'group') {
          redirectPath = `/my-account/sessions?booking_success=true&booking_id=${result.bookingId}`;
        } else {
          redirectPath = '/my-account/sessions';
        }

        sessionStorage.setItem('redirect_after_login', redirectPath);
        console.log('✅ AuthContext: Set redirect_after_login =', redirectPath);

        // Clear pending booking AFTER setting redirect
        sessionStorage.removeItem('pendingBooking');
        sessionStorage.removeItem('booking_error');

        // Signal to login.tsx that booking processing is complete
        sessionStorage.setItem('booking_processing', 'complete');
        sessionStorage.removeItem('booking_creation_lock');
        console.log('✅ AuthContext: Booking flow complete - signaled booking_processing=complete, cleared lock');
      } else {
        console.error('❌ AuthContext: Booking creation failed:', result.error);
        sessionStorage.setItem('booking_error', result.error || 'Failed to create booking');
        sessionStorage.setItem('redirect_after_login', '/booking');
        // Signal completion even on error so login.tsx stops polling
        sessionStorage.setItem('booking_processing', 'complete');
        sessionStorage.removeItem('booking_creation_lock');
        console.log('🔓 AuthContext: Cleared booking creation lock (on error)');
      }
    } catch (error: any) {
      console.error('❌ AuthContext: Booking processing error:', error);
      sessionStorage.setItem('booking_error', error.message || 'Booking timeout - please try again');
      sessionStorage.setItem('redirect_after_login', '/booking');
      // Signal completion even on error so login.tsx stops polling
      sessionStorage.setItem('booking_processing', 'complete');
      sessionStorage.removeItem('booking_creation_lock');
      console.log('🔓 AuthContext: Cleared booking creation lock (on exception)');
    }
  };

  const refreshUser = async (): Promise<void> => {
    // Prevent duplicate calls within 5 seconds
    const now = Date.now();
    if (isRefreshing || (now - lastRefreshAttempt < 5000)) {
      console.log('⏭️ Skipping refresh - recent attempt in progress');
      return;
    }

    const currentToken = await storage.getToken();
    if (!currentToken) {
      console.log('⚠️ No token found during refresh');
      setUser(null);
      return;
    }

    setIsRefreshing(true);
    setLastRefreshAttempt(now);

    try {
      // Use consolidated endpoint to reduce API calls
      const response = await fetch('/api/user/context', {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Accept': 'application/json'
        },
      });

      // Check content type before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn('⚠️ /api/user/context returned non-JSON response:', {
          status: response.status,
          contentType
        });

        // If we got HTML, the backend might have crashed or routed incorrectly
        // Try to read the response text for debugging
        try {
          const text = await response.text();
          console.error('❌ Non-JSON response body (first 200 chars):', text.substring(0, 200));
        } catch (e) {
          console.error('❌ Could not read response body');
        }

        // If unauthorized, try to refresh token
        if (response.status === 401 || response.status === 403) {
          console.log('🔄 Access token expired/invalid - attempting refresh');
          const refreshed = await refreshToken();
          if (!refreshed) {
            console.log('❌ Token refresh failed - logging out');
            await logout();
          }
        } else {
          // For other errors, log out to be safe
          console.log('❌ Unexpected response from server - logging out for safety');
          await logout();
        }
        return;
      }

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        await storage.saveUser(userData);
        console.log('✅ User data refreshed successfully');
      } else if (response.status === 401 || response.status === 403) {
        // Token is invalid or expired, attempt to refresh ONCE
        console.log('🔄 Access token expired/invalid - attempting refresh');
        const refreshed = await refreshToken();
        if (!refreshed) {
          console.log('❌ Token refresh failed - logging out');
          await logout();
        } else {
          // After successful refresh, retry getting user data
          console.log('✅ Token refreshed, retrying user data fetch');
          const retryResponse = await fetch('/api/user/context', { // Use the correct endpoint here
            headers: {
              'Authorization': `Bearer ${await storage.getToken()}`,
              'Accept': 'application/json'
            },
          });

          const retryContentType = retryResponse.headers.get('content-type');
          if (retryResponse.ok && retryContentType?.includes('application/json')) {
            const userData = await retryResponse.json();
            setUser(userData);
            await storage.saveUser(userData);
            console.log('✅ User data refreshed after token renewal');
          } else {
            console.warn('⚠️ Retry response invalid, keeping session');
          }
        }
      } else {
        console.warn('⚠️ Server error from /api/user/context:', response.status, '- keeping session');
      }
    } catch (error) {
      console.error('❌ Failed to refresh user:', error);
      console.log('⚠️ Network error during refresh - keeping current session');
    } finally {
      setIsRefreshing(false);
    }
  };


  // Helper to verify auth tokens are persisted in storage
  const verifyAuthPersisted = async (): Promise<boolean> => {
    const token = await storage.getToken();
    const user = await storage.getUser();

    if (!token || !user) {
      console.warn('⚠️ Auth not persisted on first check, retrying...');

      // Retry once after 200ms
      await new Promise(resolve => setTimeout(resolve, 200));
      const retryToken = await storage.getToken();
      const retryUser = await storage.getUser();

      if (!retryToken || !retryUser) {
        console.error('❌ Auth persistence verification failed');
        return false;
      }

      console.log('✅ Auth persisted after retry');
      return true;
    }

    console.log('✅ Auth persistence verified');
    return true;
  };

  const logoutAllSessions = async (): Promise<{ success: boolean; error?: string }> => {
    const currentToken = await storage.getToken();
    if (!currentToken) {
      return { success: false, error: "No active session" };
    }

    try {
      const response = await fetch('/api/auth/logout-all', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Clear client-side state after successful server logout
        setUser(null);
        setToken(null);
        sessionStorage.removeItem('biometricAutoTriggered');
        await storage.clearAll();
        // Clear refresh timer
        if (refreshTimerId) {
          clearTimeout(refreshTimerId);
          setRefreshTimerId(null);
        }

        console.log('✅ All sessions logged out successfully');
        return { success: true };
      } else {
        // Handle non-JSON responses gracefully
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.error('❌ Logout all sessions response was not JSON:', { status: response.status, contentType });
          const errorText = await response.text();
          console.error('Logout all sessions response body:', errorText.substring(0, 200)); // Log first 200 chars
          return { success: false, error: 'Received an unexpected response from the server.' };
        }
        const data = await response.json();
        return { success: false, error: data.message || 'Failed to logout all sessions' };
      }
    } catch (error) {
      console.error('Logout all sessions error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated,
    setUser,
    setToken,
    login,
    register,
    loginWithOAuth,
    logout,
    logoutAllSessions,
    refreshUser,
    refreshToken,
  };

  // Critical: Don't render ANYTHING (including Provider) until initialization completes
  // This prevents lazy-loaded components from calling useAuth() prematurely
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-sage-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  // Only provide context AFTER initialization is complete
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};